import * as THREE from "three";
import {
  BodyArea,
  EngineLocation,
  UNCATEGORIZED_LOCATION,
} from "@bennara/shared";

/**
 * Normalize a name for matching: lowercase, strip prefixes like "mixamorig:", collapse separators.
 */
function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/^[^:]+:/, "") // strip prefix like "mixamorig:"
    .replace(/[\s\-_]+/g, ""); // collapse to alphanumeric for flexible matching
}

/**
 * Check if normalized object name matches a body area (e.g. "leftarm" matches "left_arm").
 */
function nameMatchesLocation(normalized: string, area: BodyArea): boolean {
  const locationKey = area.replace(/_/g, ""); // "left_arm" -> "leftarm"
  return normalized === locationKey || normalized.includes(locationKey);
}

/**
 * Extract a body area from a Three.js object by walking its hierarchy.
 * Checks the object and all ancestors for names that match `BodyArea` values.
 * Returns `UNCATEGORIZED_LOCATION` when nothing matches.
 */
export function getLocationFromObject(object: THREE.Object3D): EngineLocation {
  const areas = Object.values(BodyArea);

  let current: THREE.Object3D | null = object;
  while (current) {
    const name = current.name?.trim();
    if (name) {
      const normalized = normalizeName(name);
      for (const area of areas) {
        if (nameMatchesLocation(normalized, area)) {
          return area;
        }
      }
    }
    current = current.parent;
  }

  return UNCATEGORIZED_LOCATION;
}

/**
 * Find an object in the model that matches a body area (e.g. left_arm, torso).
 * Uses the same matching logic as getLocationFromObject.
 */
export function findObjectByLocation(
  root: THREE.Object3D,
  area: BodyArea,
): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  root.traverse((child) => {
    if (found) return;
    const name = child.name?.trim();
    if (name && nameMatchesLocation(normalizeName(name), area)) {
      found = child;
    }
  });
  return found;
}
